// Locale setup: English (default, unprefixed), Hindi (/hi), Arabic (/ar) and
// Russian (/ru).
//
// Arabic is right-to-left: _document sets dir from the route at export time and
// _app re-syncs it on client-side navigation, with the RTL overrides living in
// public/assets/css/rtl.css.
//
// Blog pages are deliberately NOT localized — posts stay in whatever language
// they were written in, so /blog and /blog/* exist only once, unprefixed, and
// carry no hreflang alternates. See isLocalizablePath below.

export const DEFAULT_LOCALE = "en";

export type LocaleCode = "en" | "hi" | "ar" | "ru";

export type Locale = {
  code: LocaleCode;
  label: string;
  nativeLabel: string;
  dir: "ltr" | "rtl";
  /** BCP-47 tag for html lang and hreflang. */
  htmlLang: string;
};

export const LOCALES: Locale[] = [
  { code: "en", label: "English", nativeLabel: "English", dir: "ltr", htmlLang: "en" },
  { code: "hi", label: "Hindi", nativeLabel: "हिन्दी", dir: "ltr", htmlLang: "hi" },
  { code: "ar", label: "Arabic", nativeLabel: "العربية", dir: "rtl", htmlLang: "ar" },
  { code: "ru", label: "Russian", nativeLabel: "Русский", dir: "ltr", htmlLang: "ru" },
];

export const TARGET_LOCALES = LOCALES.filter((l) => l.code !== DEFAULT_LOCALE);
export const LOCALE_CODES = LOCALES.map((l) => l.code);

export function getLocaleMeta(code: string): Locale {
  return LOCALES.find((l) => l.code === code) ?? LOCALES[0];
}

/**
 * Blog routes are single-language by design, so they are never prefixed and
 * never offered in the language switcher's alternates.
 */
export function isLocalizablePath(path: string): boolean {
  const bare = stripLocaleFromPath(path);
  return !(bare === "/blog" || bare.startsWith("/blog/"));
}

export function stripLocaleFromPath(pathname = "/"): string {
  const [beforeHash, hash = ""] = pathname.split("#");
  const [beforeQuery, query = ""] = beforeHash.split("?");
  const segments = beforeQuery.split("/").filter(Boolean);

  if (segments.length && segments[0] !== DEFAULT_LOCALE && LOCALE_CODES.includes(segments[0] as LocaleCode)) {
    segments.shift();
  }

  const bare = `/${segments.join("/")}`.replace(/\/$/, "") || "/";
  return `${bare}${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
}

/**
 * Rewrites an internal path for a locale. External links, anchors, mailto/tel
 * and blog routes are returned unchanged.
 */
export function localizePath(
  pathname = "/",
  locale: string = DEFAULT_LOCALE,
  /**
   * Blog routes are refused a locale prefix by default. A post authored in the
   * CMS does have translated routes, so the blog detail page passes true here.
   */
  force = false
): string {
  if (!pathname || /^(https?:|mailto:|tel:|#)/.test(pathname)) return pathname;

  const base = stripLocaleFromPath(pathname);
  if (locale === DEFAULT_LOCALE) return base;
  if (!force && !isLocalizablePath(base)) return base;

  return base === "/" ? `/${locale}` : `/${locale}${base}`;
}
